import React, { useEffect, useMemo, useState } from 'react';
import styles from './Subscribe.module.scss';
import SubscriptionOffering from '../../SubscriptionOffering/SubscriptionOffering';
import SubscriptionMarketSelector from '../../SubscriptionMarketSelector/SubscriptionMarketSelector';
import { useSubscriptionStatus } from '../../../providers/subscription-status.provider';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchSubscriptionPlans } from '../../../services/payment.service';
import type { SubscriptionPlan } from '../../../types/payment.types';
import { sanitizePlans } from '../../../utils/subscription-plans';
import heroImage from '../../../assets/images/home/home-banner-3000x780-hq.jpg';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { useAnalyticsService } from '../../../services/analytics.service';
import { ANALYTICS_EVENT_TYPES } from '../../../types/analytics.types';
import {
    isSubscriptionCheckoutRecoverable,
    requiresSubscriptionManagement
} from '../../../utils/subscription';
import { Routes } from '../../../types/constants';
import { buildJoinUrl } from '../../../utils/join';
import { useSubscriptionMarket } from '../../../hooks/useSubscriptionMarket';

const Subscribe = () => {
    const { hasActiveSubscription, isSubscriptionLoading, subscription } = useSubscriptionStatus();
    const { isLoggedIn } = useUserState();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { trackEvent } = useAnalyticsService();
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [planStatus, setPlanStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const requestedMarketCode = searchParams.get('market')?.trim() ?? '';
    const { markets, selectedMarket, selectMarket, status: marketStatus } = useSubscriptionMarket(requestedMarketCode);
    const selectedMarketCode = selectedMarket?.code ?? '';
    const hasRecoverableCheckout = isSubscriptionCheckoutRecoverable(subscription);
    const hasManagedSubscription = hasActiveSubscription || requiresSubscriptionManagement(subscription);

    useEffect(() => {
        void trackEvent({
            eventType: ANALYTICS_EVENT_TYPES.SUBSCRIPTION_PRICING_VIEWED,
            metadata: { source: 'pricing_page' }
        });
    }, [trackEvent]);

    useEffect(() => {
        if (marketStatus === 'error') {
            setPlans([]);
            setPlanStatus('error');
            return;
        }
        if (marketStatus !== 'ready' || !selectedMarketCode) return;

        let isMounted = true;
        setPlanStatus('loading');

        fetchSubscriptionPlans(selectedMarketCode)
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                const sanitizedPlans = sanitizePlans(data);
                if (!sanitizedPlans.length) {
                    setPlans([]);
                    setPlanStatus('error');
                    return;
                }
                setPlans(sanitizedPlans);
                setPlanStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Subscription plans fetch failed', { error });
                setPlans([]);
                setPlanStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, [marketStatus, selectedMarketCode]);

    const handleMarketChange = (marketCode: string) => {
        selectMarket(marketCode);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('market', marketCode.toLowerCase());
        setSearchParams(nextParams, { replace: true });
    };

    const handlePlanSelect = (offering: SubscriptionPlan) => {
        void trackEvent({
            eventType: ANALYTICS_EVENT_TYPES.SUBSCRIPTION_PLAN_SELECTED,
            value: offering.price,
            currency: offering.currency.toUpperCase(),
            metadata: {
                source: isLoggedIn ? 'pricing_page' : 'pricing_page_register_redirect',
                offeringId: offering.offeringId,
                marketCode: offering.marketCode,
                duration: offering.duration
            }
        });

        navigate(buildJoinUrl({
            offeringId: offering.offeringId,
            market: offering.marketCode,
            source: 'pricing'
        }));
    };

    const statusCardContent = useMemo(() => {
        if (isSubscriptionLoading) {
            return (
                <p className={styles.statusCopy}>Checking your membership status...</p>
            );
        }

        if (hasManagedSubscription) {
            return (
                <>
                    <p className={styles.statusCopy}>
                        {hasActiveSubscription
                            ? 'You are currently subscribed and ready to watch.'
                            : 'Your subscription needs billing attention before you can subscribe again.'}
                    </p>
                    <Link className={styles.statusLink} to={Routes.PROFILE_BILLING}>
                        Manage your subscription
                    </Link>
                </>
            );
        }

        return (
            <>
                <p className={styles.statusCopy}>Join now to unlock premium fights and interviews on demand.</p>
                <Link
                    className={styles.statusLink}
                    to={buildJoinUrl({ market: selectedMarketCode, source: 'pricing' })}
                >
                    Start membership
                </Link>
            </>
        );
    }, [hasActiveSubscription, hasManagedSubscription, isSubscriptionLoading, selectedMarketCode]);

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Membership</span>
                    <h1 className={styles.heroTitle}>Every fight. Every moment. One subscription.</h1>
                    <p className={styles.heroSubtitle}>
                        Stream Combat TV on any device, cancel when you need to, and keep your front-row seat
                        to the biggest cards across boxing, MMA, kickboxing, and more.
                    </p>
                    <ul className={styles.heroHighlights}>
                        <li>Full fight access + interviews on demand</li>
                        <li>Member-first access to exclusive coverage</li>
                        <li>Simple billing and cancellation from your account</li>
                    </ul>
                </div>
                <div className={styles.heroVisual}>
                    <img src={heroImage} alt="Combat TV fighters" className={styles.heroImage} />
                </div>
            </section>

            {!isLoggedIn ? (
                <section className={styles.statusSection}>
                    <div className={styles.statusCard}>
                        <div>
                            <span className={styles.statusEyebrow}>Membership status</span>
                            <h2 className={styles.statusTitle}>{hasActiveSubscription ? 'You are all set' : 'Ready to become a member?'}</h2>
                            {statusCardContent}
                        </div>
                    </div>
                    <div className={styles.statusSupport}>
                        <p>Questions about your membership or billing?</p>
                        <Link className={styles.statusLink} to="/contact">Contact support</Link>
                    </div>
                </section>
            ) : null}

            <section className={styles.plansSection}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionTag}>Plans</span>
                    <h2 className={styles.sectionTitle}>Choose the plan that fits your fight schedule.</h2>
                    <p className={styles.sectionSubtitle}>All memberships include unlimited access across devices.</p>
                    {markets.length > 1 && selectedMarket ? (
                        <div className={styles.marketSelector}>
                            <SubscriptionMarketSelector
                                markets={markets}
                                selectedMarketCode={selectedMarket.code}
                                onChange={handleMarketChange}
                            />
                        </div>
                    ) : null}
                </div>

                {planStatus === 'loading' ? (
                    <div className={styles.planGrid} aria-busy="true">
                        {Array.from({ length: 2 }).map((_, index) => (
                            <div key={`plan-skeleton-${index}`} className={styles.planSkeleton} />
                        ))}
                    </div>
                ) : null}

                {planStatus === 'error' ? (
                    <div className={styles.notice} role="alert">
                        Plans are temporarily unavailable. We could not verify current pricing, so checkout has not
                        been opened.
                    </div>
                ) : null}

                {!isSubscriptionLoading && hasRecoverableCheckout ? (
                    <div className={styles.notice} role="status">
                        Your previous payment was not completed. Choose the same plan below to reopen checkout,
                        correct your payment details, and try again.
                    </div>
                ) : null}

                {!isSubscriptionLoading && hasManagedSubscription ? (
                    <div className={styles.activeNotice}>
                        <p>Manage your plan or update billing anytime from your subscription dashboard.</p>
                        <Link className={styles.statusLink} to={Routes.PROFILE_BILLING}>Go to billing</Link>
                    </div>
                ) : planStatus !== 'success' ? null : (
                    <div className={styles.planGrid}>
                        {plans.map((offering) => (
                            <SubscriptionOffering
                                key={offering.offeringId || offering.duration}
                                offering={offering}
                                onSelect={handlePlanSelect}
                            />
                        ))}
                    </div>
                )}
            </section>
        </main>
    );
};

export default Subscribe;
